import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { onAuthStateChanged, getAuth, User } from "firebase/auth";
import {
  FaGraduationCap,
  FaBriefcase,
  FaPlus,
  FaEdit,
  FaTrash,
} from "react-icons/fa";
import { FiX } from "react-icons/fi";
import { db } from "../firebase";
import { ScrollAnimation, StaggerContainer, StaggerItem, ParallaxScroll } from "./ScrollAnimation";

interface EducationExperienceProps {
  id: string;
  title: string;
  institution?: string;
  company?: string;
  period: string;
  imageUrl: string;
}

const EducationExperience: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"education" | "experience">("education");
  const [education, setEducation] = useState<EducationExperienceProps[]>([]);
  const [experience, setExperience] = useState<EducationExperienceProps[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formType, setFormType] = useState<"education" | "experience" | null>(null);
  const [formData, setFormData] = useState<EducationExperienceProps>({
    id: "",
    title: "",
    institution: "",
    company: "",
    period: "",
    imageUrl: "",
  });
  const [file, setFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);

  const storage = getStorage();
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const educationSnapshot = await getDocs(collection(db, "education"));
      const experienceSnapshot = await getDocs(collection(db, "experience"));
      setEducation(
        educationSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as EducationExperienceProps[]
      );
      setExperience(
        experienceSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as EducationExperienceProps[]
      );
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleImageUpload = async (): Promise<string> => {
    if (!file) return formData.imageUrl || "";
    const storageRef = ref(storage, `${formType}/${file.name}`);
    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const { id, title, institution, company, period } = formData;
    try {
      const imageUrl = await handleImageUpload();
      if (id) {
        const collectionName = formType === "education" ? "education" : "experience";
        const docRef = doc(db, collectionName, id);
        await updateDoc(docRef, { title, institution, company, period, imageUrl });
      } else {
        const collectionName = formType === "education" ? "education" : "experience";
        const docRef = collection(db, collectionName);
        await addDoc(docRef, { title, institution, company, period, imageUrl });
      }
      fetchData();
      setFormData({ id: "", title: "", institution: "", company: "", period: "", imageUrl: "" });
      setFile(null);
      setImagePreview(null);
      setFormType(null);
    } catch (error) {
      console.error("Error adding/updating document:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string, type: "education" | "experience") => {
    try {
      const docRef = doc(db, type, id);
      await deleteDoc(docRef);
      fetchData();
    } catch (error) {
      console.error("Error deleting document:", error);
    }
  };

  const data = activeTab === "education" ? education : experience;

  if (loading || isSubmitting) {
    return (
      <div className="flex justify-center items-center py-40">
        <div className="w-10 h-10 border-4 border-primary-200 dark:border-primary-900 border-t-primary-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <section className="section-padding relative" id="education-experience">
      {/* Background decoration with parallax */}
      <ParallaxScroll offset={25}>
        <div className="absolute top-1/2 left-0 w-72 h-72 bg-primary-500/5 rounded-full blur-3xl -translate-y-1/2 -translate-x-1/2" />
      </ParallaxScroll>
      <ParallaxScroll offset={-35}>
        <div className="absolute top-1/4 right-0 w-64 h-64 bg-accent-500/5 rounded-full blur-3xl translate-x-1/2" />
      </ParallaxScroll>

      <div className="max-w-6xl mx-auto relative">
        {/* Section Header with enhanced animation */}
        <ScrollAnimation variant="slideUp" delay={0.1} duration={0.7}>
          <div className="text-center mb-16">
            <motion.div
              className="section-badge"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              My Journey
            </motion.div>
            <h2 className="section-title">
              Education & <span className="gradient-text">Experience</span>
            </h2>
            <p className="section-subtitle">
              My academic background and professional journey
            </p>
          </div>
        </ScrollAnimation>

        {/* Tab Switcher */}
        <motion.div
          className="flex justify-center mb-12"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          <div className="inline-flex bg-gray-100 dark:bg-surface-800 rounded-2xl p-1.5 relative">
            {/* Animated background pill */}
            <motion.div
              className="absolute top-1.5 bottom-1.5 rounded-xl bg-white dark:bg-surface-900 shadow-md"
              layout
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              style={{
                left: activeTab === "education" ? "6px" : "50%",
                right: activeTab === "experience" ? "6px" : "50%",
              }}
            />
            <button
              onClick={() => setActiveTab("education")}
              className={`relative z-10 flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-colors duration-300 ${
                activeTab === "education"
                  ? "text-primary-600 dark:text-primary-400"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              }`}
            >
              <FaGraduationCap size={16} />
              Education
            </button>
            <button
              onClick={() => setActiveTab("experience")}
              className={`relative z-10 flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-colors duration-300 ${
                activeTab === "experience"
                  ? "text-primary-600 dark:text-primary-400"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              }`}
            >
              <FaBriefcase size={16} />
              Experience
            </button>
          </div>
        </motion.div>

        {/* Add Button */}
        {user && (
          <motion.div
            className="flex justify-end mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <motion.button
              className="btn-primary !py-2.5 !px-5 !text-sm"
              onClick={() => {
                setFormType(activeTab);
                setFormData({ id: "", title: "", institution: "", company: "", period: "", imageUrl: "" });
                setImagePreview(null);
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaPlus size={12} />
              Add {activeTab === "education" ? "Education" : "Experience"}
            </motion.button>
          </motion.div>
        )}

        {/* Cards Grid with AnimatePresence for tab switching */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {data.map((item, index) => (
              <motion.div
                key={item.id}
                className="glass-card overflow-hidden group relative"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
              >
                {/* Glow effect on hover */}
                <div className="absolute -inset-0.5 bg-gradient-to-r from-primary-500 to-accent-500 rounded-2xl opacity-0 group-hover:opacity-20 blur-sm transition-opacity duration-500" />

                <div className="relative bg-white dark:bg-surface-800 rounded-2xl overflow-hidden">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={item.imageUrl}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

                    {/* Shine effect on hover */}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                    </div>

                    {/* Admin Actions - Mobile-friendly */}
                    {user && (
                      <div className="absolute top-3 right-3 z-20">
                        <div className="admin-controls">
                          <motion.button
                            className="btn-icon-sm"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => {
                              setFormType(activeTab);
                              setFormData({
                                ...item,
                                company: item.company || "",
                                institution: item.institution || "",
                              });
                              setImagePreview(item.imageUrl);
                            }}
                            aria-label={`Edit ${activeTab} entry`}
                          >
                            <FaEdit size={14} />
                          </motion.button>
                          <motion.button
                            className="btn-icon-sm btn-danger"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleDelete(item.id, activeTab)}
                            aria-label={`Delete ${activeTab} entry`}
                          >
                            <FaTrash size={14} />
                          </motion.button>
                        </div>
                      </div>
                    )}

                    {/* Period Badge */}
                    <motion.div
                      className="absolute bottom-3 left-3"
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 + 0.3 }}
                    >
                      <span className="px-3 py-1.5 bg-white/90 dark:bg-surface-800/90 backdrop-blur-sm rounded-full text-xs font-semibold text-primary-600 dark:text-primary-400 shadow-sm border border-white/20">
                        {item.period}
                      </span>
                    </motion.div>
                  </div>

                  <div className="p-5">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300">
                      {item.title}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                      {activeTab === "education" ? (
                        <FaGraduationCap size={12} className="text-primary-500" />
                      ) : (
                        <FaBriefcase size={12} className="text-accent-500" />
                      )}
                      {activeTab === "education" ? item.institution : item.company}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        {data.length === 0 && (
          <motion.div
            className="text-center py-16 text-gray-400 dark:text-gray-600"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-surface-800 flex items-center justify-center">
              {activeTab === "education" ? (
                <FaGraduationCap size={24} className="text-gray-300 dark:text-gray-600" />
              ) : (
                <FaBriefcase size={24} className="text-gray-300 dark:text-gray-600" />
              )}
            </div>
            <p>No {activeTab} entries yet.</p>
          </motion.div>
        )}
      </div>

      {/* Modal */}
      <AnimatePresence>
        {formType && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              setFormType(null);
              setImagePreview(null);
            }}
          >
            <motion.div
              className="modal-content"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                {formData.id ? "Edit" : "Add"} {formType === "education" ? "Education" : "Experience"}
              </h2>
              <form onSubmit={handleFormSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Title
                  </label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>
                {formType === "education" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      Institution
                    </label>
                    <input
                      type="text"
                      className="input-field"
                      value={formData.institution || ""}
                      onChange={(e) => setFormData({ ...formData, institution: e.target.value })}
                    />
                  </div>
                )}
                {formType === "experience" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      Company
                    </label>
                    <input
                      type="text"
                      className="input-field"
                      value={formData.company || ""}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Period
                  </label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.period}
                    onChange={(e) => setFormData({ ...formData, period: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    className="input-field !py-2"
                    onChange={(e) => {
                      const selectedFile = e.target.files ? e.target.files[0] : null;
                      setFile(selectedFile);
                      if (selectedFile) {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setImagePreview(reader.result as string);
                        };
                        reader.readAsDataURL(selectedFile);
                      }
                    }}
                  />

                  {/* Image Preview */}
                  {(imagePreview || formData.imageUrl) && (
                    <div className="mt-3 relative">
                      <img
                        src={imagePreview || formData.imageUrl}
                        alt="Upload preview"
                        className="w-full h-40 object-cover rounded-xl border-2 border-gray-200 dark:border-gray-700"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setFile(null);
                          setImagePreview(null);
                          setFormData({ ...formData, imageUrl: "" });
                        }}
                        className="absolute top-2 right-2 btn-icon-sm btn-danger"
                        aria-label="Remove image"
                      >
                        <FiX size={16} />
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    className="btn-secondary !py-2.5 !px-5 !text-sm"
                    onClick={() => {
                      setFormType(null);
                      setImagePreview(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary !py-2.5 !px-5 !text-sm">
                    Save
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default EducationExperience;
